<template>
  <section class="products-filter" :class="{ 'products-filter--rtl': isRtl }" :dir="textDirection">
    <div class="filter-card">
      <div class="filter-card__head">
        <div class="filter-card__title">
          <v-icon small class="filter-card__title-icon">fal fa-filter</v-icon>
          <span>{{ $t("products.filtersTitle") }}</span>
          <span v-if="activeFiltersCount" class="filter-card__badge">
            {{ activeFiltersCount }}
          </span>
        </div>
        <div class="filter-card__actions">
          <button type="button" class="filter-link-btn" @click="expanded = !expanded">
            <v-icon x-small>{{ expanded ? "fal fa-chevron-up" : "fal fa-chevron-down" }}</v-icon>
          </button>
          <button
            type="button"
            class="filter-link-btn filter-link-btn--reset"
            :disabled="!activeFiltersCount"
            @click="resetFilters"
          >
            {{ $t("products.resetFilters") }}
          </button>
        </div>
      </div>

      <form v-show="expanded" class="filter-body" @submit.prevent="applyFilters">
        <div class="filter-section">
          <div class="filter-section__grid">
            <div class="filter-control">
              <label class="filter-control__label">{{ $t("forms.labels.name_ar") }}</label>
              <div class="filter-control__input">
                <v-text-field
                  v-model="frmData.name_ar"
                  dense
                  outlined
                  hide-details
                  clearable
                  @input="debouncedApply"
                />
              </div>
            </div>

            <div class="filter-control">
              <label class="filter-control__label">{{ $t("forms.labels.name_en") }}</label>
              <div class="filter-control__input">
                <v-text-field
                  v-model="frmData.name_en"
                  dense
                  outlined
                  hide-details
                  clearable
                  @input="debouncedApply"
                />
              </div>
            </div>

            <div class="filter-control">
              <label class="filter-control__label">{{ $t("forms.labels.desc_ar") }}</label>
              <div class="filter-control__input">
                <v-text-field
                  v-model="frmData.desc_ar"
                  dense
                  outlined
                  hide-details
                  clearable
                  @input="debouncedApply"
                />
              </div>
            </div>

            <div class="filter-control">
              <label class="filter-control__label">{{ $t("forms.labels.desc_en") }}</label>
              <div class="filter-control__input">
                <v-text-field
                  v-model="frmData.desc_en"
                  dense
                  outlined
                  hide-details
                  clearable
                  @input="debouncedApply"
                />
              </div>
            </div>

            <div class="filter-control">
              <label class="filter-control__label">{{ $t("titles.code") }}</label>
              <div class="filter-control__input">
                <v-text-field
                  v-model="frmData.code"
                  dense
                  outlined
                  hide-details
                  clearable
                  @input="debouncedApply"
                />
              </div>
            </div>
          </div>

          <div class="filter-section__grid filter-section__grid--sub">
            <div class="filter-control filter-control--select">
              <label class="filter-control__label">{{ $t("products.filterHasNameAr") }}</label>
              <div class="filter-control__input">
                <multiselect
                  v-model="frmData.has_name_ar"
                  :options="booleanOptions"
                  label="name"
                  track-by="value"
                  :show-labels="false"
                  :searchable="false"
                  :allow-empty="false"
                  @input="(v) => onBooleanInput('has_name_ar', v)"
                />
              </div>
            </div>

            <div class="filter-control filter-control--select">
              <label class="filter-control__label">{{ $t("products.filterHasNameEn") }}</label>
              <div class="filter-control__input">
                <multiselect
                  v-model="frmData.has_name_en"
                  :options="booleanOptions"
                  label="name"
                  track-by="value"
                  :show-labels="false"
                  :searchable="false"
                  :allow-empty="false"
                  @input="(v) => onBooleanInput('has_name_en', v)"
                />
              </div>
            </div>

            <div class="filter-control filter-control--select">
              <label class="filter-control__label">{{ $t("products.filterHasDescAr") }}</label>
              <div class="filter-control__input">
                <multiselect
                  v-model="frmData.has_desc_ar"
                  :options="booleanOptions"
                  label="name"
                  track-by="value"
                  :show-labels="false"
                  :searchable="false"
                  :allow-empty="false"
                  @input="(v) => onBooleanInput('has_desc_ar', v)"
                />
              </div>
            </div>

            <div class="filter-control filter-control--select">
              <label class="filter-control__label">{{ $t("products.filterHasDescEn") }}</label>
              <div class="filter-control__input">
                <multiselect
                  v-model="frmData.has_desc_en"
                  :options="booleanOptions"
                  label="name"
                  track-by="value"
                  :show-labels="false"
                  :searchable="false"
                  :allow-empty="false"
                  @input="(v) => onBooleanInput('has_desc_en', v)"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="filter-section">
          <div class="filter-section__grid">
            <div class="filter-control filter-control--select filter-control--category">
              <label class="filter-control__label filter-control__label--category">
                <v-icon x-small class="filter-control__label-icon">fal fa-sitemap</v-icon>
                <span>{{ $t("products.filterCategory") }}</span>
              </label>
              <div class="filter-control__input filter-control__input--treeselect">
                <treeselect
                  class="filter-category-treeselect"
                  :class="{ 'filter-category-treeselect--filtered': isActiveCategoryFilter(frmData.category_id) }"
                  :dir="textDirection"
                  :multiple="false"
                  :options="categoryTreeOptions"
                  :disable-branch-nodes="true"
                  :show-count="true"
                  :searchable="false"
                  :clearable="false"
                  :loading="categoriesLoading"
                  v-model="frmData.category_id"
                  @input="onCategoryInput"
                />
              </div>
            </div>

            <div class="filter-control filter-control--select">
              <label class="filter-control__label">{{ $t("products.filterBrand") }}</label>
              <div class="filter-control__input">
                <multiselect
                  v-model="frmData.brand"
                  :options="brandOptions"
                  label="name"
                  track-by="id"
                  :show-labels="false"
                  :searchable="true"
                  :allow-empty="false"
                  :loading="brandsLoading"
                  @input="onBrandInput"
                />
              </div>
            </div>

            <div class="filter-control filter-control--select">
              <label class="filter-control__label">{{ $t("forms.labels.status") }}</label>
              <div class="filter-control__input">
                <multiselect
                  v-model="frmData.is_active"
                  :options="booleanOptions"
                  label="name"
                  track-by="value"
                  :show-labels="false"
                  :searchable="false"
                  :allow-empty="false"
                  @input="(v) => onBooleanInput('is_active', v)"
                />
              </div>
            </div>

            <div class="filter-control filter-control--select">
              <label class="filter-control__label">{{ $t("products.filterMostOrdered") }}</label>
              <div class="filter-control__input">
                <multiselect
                  v-model="frmData.is_most_order"
                  :options="booleanOptions"
                  label="name"
                  track-by="value"
                  :show-labels="false"
                  :searchable="false"
                  :allow-empty="false"
                  @input="(v) => onBooleanInput('is_most_order', v)"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="filter-section">
          <div class="filter-section__grid">
            <div class="filter-control">
              <label class="filter-control__label">{{ $t("products.filterPriceFrom") }}</label>
              <div class="filter-control__input">
                <v-text-field
                  v-model="frmData.from_price"
                  type="number"
                  min="0"
                  dense
                  outlined
                  hide-details
                  clearable
                  @input="debouncedApply"
                />
              </div>
            </div>

            <div class="filter-control">
              <label class="filter-control__label">{{ $t("products.filterPriceTo") }}</label>
              <div class="filter-control__input">
                <v-text-field
                  v-model="frmData.to_price"
                  type="number"
                  min="0"
                  dense
                  outlined
                  hide-details
                  clearable
                  @input="debouncedApply"
                />
              </div>
            </div>

            <div class="filter-control">
              <label class="filter-control__label">{{ $t("products.filterOrdering") }}</label>
              <div class="filter-control__input">
                <v-text-field
                  v-model="frmData.ordering"
                  type="number"
                  dense
                  outlined
                  hide-details
                  clearable
                  @input="debouncedApply"
                />
              </div>
            </div>

            <div class="filter-control filter-control--select">
              <label class="filter-control__label">{{ $t("products.filterHasPrice") }}</label>
              <div class="filter-control__input">
                <multiselect
                  v-model="frmData.has_price"
                  :options="booleanOptions"
                  label="name"
                  track-by="value"
                  :show-labels="false"
                  :searchable="false"
                  :allow-empty="false"
                  @input="(v) => onBooleanInput('has_price', v)"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="filter-section filter-section--last">
          <div class="filter-section__grid">
            <div class="filter-control filter-control--select">
              <label class="filter-control__label">{{ $t("products.filterPrescription") }}</label>
              <div class="filter-control__input">
                <multiselect
                  v-model="frmData.prescription_requirement"
                  :options="booleanOptions"
                  label="name"
                  track-by="value"
                  :show-labels="false"
                  :searchable="false"
                  :allow-empty="false"
                  @input="(v) => onBooleanInput('prescription_requirement', v)"
                />
              </div>
            </div>

            <div class="filter-control filter-control--select">
              <label class="filter-control__label">{{ $t("products.filterHasImage") }}</label>
              <div class="filter-control__input">
                <multiselect
                  v-model="frmData.has_image"
                  :options="booleanOptions"
                  label="name"
                  track-by="value"
                  :show-labels="false"
                  :searchable="false"
                  :allow-empty="false"
                  @input="(v) => onBooleanInput('has_image', v)"
                />
              </div>
            </div>

            <div class="filter-control filter-control--select">
              <label class="filter-control__label">{{ $t("products.filterHasOffer") }}</label>
              <div class="filter-control__input">
                <multiselect
                  v-model="frmData.has_offer"
                  :options="booleanOptions"
                  label="name"
                  track-by="value"
                  :show-labels="false"
                  :searchable="false"
                  :allow-empty="false"
                  @input="(v) => onBooleanInput('has_offer', v)"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="filter-footer">
          <button type="submit" class="filter-apply-btn">
            <v-icon x-small>fal fa-search</v-icon>
            {{ $t("products.applyFilters") }}
          </button>
        </div>
      </form>
    </div>
  </section>
</template>

<script>
import _ from "lodash";
import { mapGetters } from "vuex";

const FILTER_ALL_VALUE = "__all__";

const QUERY_KEYS = [
  "keyword",
  "name_ar",
  "name_en",
  "desc_ar",
  "desc_en",
  "has_name_ar",
  "has_name_en",
  "has_desc_ar",
  "has_desc_en",
  "code",
  "is_active",
  "is_most_order",
  "category_id",
  "brand_id",
  "from_price",
  "to_price",
  "ordering",
  "prescription_requirement",
  "has_image",
  "has_offer",
  "has_price",
];

export default {
  data() {
    return {
      expanded: true,
      categoriesLoading: false,
      brandsLoading: false,
      categoryTree: [],
      brands: [],
      frmData: {
        name_ar: "",
        name_en: "",
        desc_ar: "",
        desc_en: "",
        code: "",
        has_name_ar: null,
        has_name_en: null,
        has_desc_ar: null,
        has_desc_en: null,
        is_active: null,
        is_most_order: null,
        category_id: FILTER_ALL_VALUE,
        brand: null,
        from_price: "",
        to_price: "",
        ordering: "",
        prescription_requirement: null,
        has_image: null,
        has_offer: null,
        has_price: null,
      },
    };
  },

  computed: {
    ...mapGetters({
      lang: "lang_module/lang",
    }),

    isRtl() {
      return this.lang === "ar";
    },

    textDirection() {
      return this.isRtl ? "rtl" : "ltr";
    },

    allOption() {
      return { name: this.$t("products.filterAll"), value: FILTER_ALL_VALUE };
    },

    allBrandOption() {
      return { id: FILTER_ALL_VALUE, name: this.$t("products.filterAll") };
    },

    brandOptions() {
      return [this.allBrandOption, ...this.brands];
    },

    categoryTreeOptions() {
      return [
        { id: FILTER_ALL_VALUE, label: this.$t("products.filterAll") },
        ...this.categoryTree,
      ];
    },

    booleanOptions() {
      return [
        this.allOption,
        { name: this.$t("products.filterYes"), value: 1 },
        { name: this.$t("products.filterNo"), value: 0 },
      ];
    },

    activeFiltersCount() {
      let count = 0;
      if (this.frmData.name_ar) count++;
      if (this.frmData.name_en) count++;
      if (this.frmData.desc_ar) count++;
      if (this.frmData.desc_en) count++;
      if (this.frmData.code) count++;
      if (this.isActiveSelectFilter(this.frmData.has_name_ar)) count++;
      if (this.isActiveSelectFilter(this.frmData.has_name_en)) count++;
      if (this.isActiveSelectFilter(this.frmData.has_desc_ar)) count++;
      if (this.isActiveSelectFilter(this.frmData.has_desc_en)) count++;
      if (this.isActiveSelectFilter(this.frmData.is_active)) count++;
      if (this.isActiveSelectFilter(this.frmData.is_most_order)) count++;
      if (this.isActiveCategoryFilter(this.frmData.category_id)) count++;
      if (this.isActiveBrandFilter(this.frmData.brand)) count++;
      if (this.frmData.from_price !== "" && this.frmData.from_price != null) count++;
      if (this.frmData.to_price !== "" && this.frmData.to_price != null) count++;
      if (this.frmData.ordering !== "" && this.frmData.ordering != null) count++;
      if (this.isActiveSelectFilter(this.frmData.prescription_requirement)) count++;
      if (this.isActiveSelectFilter(this.frmData.has_image)) count++;
      if (this.isActiveSelectFilter(this.frmData.has_offer)) count++;
      if (this.isActiveSelectFilter(this.frmData.has_price)) count++;
      return count;
    },
  },

  created() {
    this.debouncedApply = _.debounce(this.applyFilters, 450);
    this.initSelectDefaults();
    this.loadLookups();
    this.syncFromRoute(this.$route.query);
  },

  watch: {
    "$route.query": {
      deep: true,
      handler(query) {
        this.syncFromRoute(query);
      },
    },
  },

  methods: {
    initSelectDefaults() {
      this.frmData.brand = this.allBrandOption;
      this.frmData.is_active = this.allOption;
      this.frmData.is_most_order = this.allOption;
      this.frmData.prescription_requirement = this.allOption;
      this.frmData.has_image = this.allOption;
      this.frmData.has_offer = this.allOption;
      this.frmData.has_price = this.allOption;
      this.frmData.has_name_ar = this.allOption;
      this.frmData.has_name_en = this.allOption;
      this.frmData.has_desc_ar = this.allOption;
      this.frmData.has_desc_en = this.allOption;
      this.frmData.category_id = FILTER_ALL_VALUE;
    },

    isActiveSelectFilter(option) {
      if (!option || option.value === FILTER_ALL_VALUE) return false;
      return option.value === 0 || option.value === 1;
    },

    isActiveBrandFilter(brand) {
      return brand?.id != null && brand.id !== FILTER_ALL_VALUE;
    },

    isActiveCategoryFilter(categoryId) {
      return categoryId != null && categoryId !== FILTER_ALL_VALUE;
    },

    onCategoryInput(value) {
      this.frmData.category_id = value == null ? FILTER_ALL_VALUE : value;
      this.applyFilters();
    },

    onBrandInput(value) {
      this.frmData.brand =
        !value || value.id === FILTER_ALL_VALUE ? this.allBrandOption : value;
      this.applyFilters();
    },

    onBooleanInput(field, value) {
      this.frmData[field] =
        !value || value.value === FILTER_ALL_VALUE ? this.allOption : value;
      this.applyFilters();
    },

    normalizeTree(nodes = []) {
      return nodes.map((node) => ({
        id: node.id,
        label: node.label || node.name,
        children: node.children?.length ? this.normalizeTree(node.children) : undefined,
      }));
    },

    loadLookups() {
      this.categoriesLoading = true;
      this.brandsLoading = true;

      Promise.all([
        this.$axios({ method: "GET", url: "category_tree" }),
        this.$axios({ method: "GET", url: "get_all_brand" }),
      ])
        .then(([categoriesRes, brandsRes]) => {
          this.categoryTree = this.normalizeTree(categoriesRes.data.data || []);
          this.brands = (brandsRes.data.data || []).map((item) => ({
            id: item.id,
            name: item.name,
          }));
          this.syncFromRoute(this.$route.query);
        })
        .finally(() => {
          this.categoriesLoading = false;
          this.brandsLoading = false;
        });
    },

    findBooleanOption(value) {
      if (value === undefined || value === null || value === "") return this.allOption;
      const numeric = Number(value);
      return this.booleanOptions.find((opt) => opt.value === numeric) || this.allOption;
    },

    syncFromRoute(query = {}) {
      this.frmData.name_ar = query.name_ar || "";
      this.frmData.name_en = query.name_en || "";
      this.frmData.desc_ar = query.desc_ar || "";
      this.frmData.desc_en = query.desc_en || "";
      this.frmData.code = query.code || "";
      this.frmData.is_active = this.findBooleanOption(query.is_active);
      this.frmData.is_most_order = this.findBooleanOption(query.is_most_order);
      this.frmData.prescription_requirement = this.findBooleanOption(
        query.prescription_requirement
      );
      this.frmData.has_image = this.findBooleanOption(query.has_image);
      this.frmData.has_offer = this.findBooleanOption(query.has_offer);
      this.frmData.has_price = this.findBooleanOption(query.has_price);
      this.frmData.has_name_ar = this.findBooleanOption(query.has_name_ar);
      this.frmData.has_name_en = this.findBooleanOption(query.has_name_en);
      this.frmData.has_desc_ar = this.findBooleanOption(query.has_desc_ar);
      this.frmData.has_desc_en = this.findBooleanOption(query.has_desc_en);
      this.frmData.from_price = query.from_price || "";
      this.frmData.to_price = query.to_price || "";
      this.frmData.ordering = query.ordering || "";
      this.frmData.category_id = query.category_id
        ? Number(query.category_id)
        : FILTER_ALL_VALUE;

      if (query.brand_id && this.brands.length) {
        this.frmData.brand =
          this.brands.find((b) => String(b.id) === String(query.brand_id)) ||
          this.allBrandOption;
      } else {
        this.frmData.brand = this.allBrandOption;
      }
    },

    buildQuery() {
      const query = { ...this.$route.query, page: 1 };

      query.name_ar = this.frmData.name_ar || undefined;
      query.name_en = this.frmData.name_en || undefined;
      query.desc_ar = this.frmData.desc_ar || undefined;
      query.desc_en = this.frmData.desc_en || undefined;
      query.code = this.frmData.code || undefined;
      query.keyword = undefined;
      query.is_active = this.isActiveSelectFilter(this.frmData.is_active)
        ? String(this.frmData.is_active.value)
        : undefined;
      query.is_most_order = this.isActiveSelectFilter(this.frmData.is_most_order)
        ? String(this.frmData.is_most_order.value)
        : undefined;
      query.category_id = this.isActiveCategoryFilter(this.frmData.category_id)
        ? String(this.frmData.category_id)
        : undefined;
      query.brand_id = this.isActiveBrandFilter(this.frmData.brand)
        ? String(this.frmData.brand.id)
        : undefined;
      query.from_price =
        this.frmData.from_price !== "" && this.frmData.from_price != null
          ? String(this.frmData.from_price)
          : undefined;
      query.to_price =
        this.frmData.to_price !== "" && this.frmData.to_price != null
          ? String(this.frmData.to_price)
          : undefined;
      query.ordering =
        this.frmData.ordering !== "" && this.frmData.ordering != null
          ? String(this.frmData.ordering)
          : undefined;
      query.prescription_requirement = this.isActiveSelectFilter(
        this.frmData.prescription_requirement
      )
        ? String(this.frmData.prescription_requirement.value)
        : undefined;
      query.has_image = this.isActiveSelectFilter(this.frmData.has_image)
        ? String(this.frmData.has_image.value)
        : undefined;
      query.has_offer = this.isActiveSelectFilter(this.frmData.has_offer)
        ? String(this.frmData.has_offer.value)
        : undefined;
      query.has_price = this.isActiveSelectFilter(this.frmData.has_price)
        ? String(this.frmData.has_price.value)
        : undefined;
      query.has_name_ar = this.isActiveSelectFilter(this.frmData.has_name_ar)
        ? String(this.frmData.has_name_ar.value)
        : undefined;
      query.has_name_en = this.isActiveSelectFilter(this.frmData.has_name_en)
        ? String(this.frmData.has_name_en.value)
        : undefined;
      query.has_desc_ar = this.isActiveSelectFilter(this.frmData.has_desc_ar)
        ? String(this.frmData.has_desc_ar.value)
        : undefined;
      query.has_desc_en = this.isActiveSelectFilter(this.frmData.has_desc_en)
        ? String(this.frmData.has_desc_en.value)
        : undefined;

      QUERY_KEYS.forEach((key) => {
        if (query[key] === undefined || query[key] === "") {
          delete query[key];
        }
      });

      return query;
    },

    applyFilters() {
      const query = this.buildQuery();
      this.$router.push({ query }).catch(() => {});
    },

    resetFilters() {
      this.frmData.name_ar = "";
      this.frmData.name_en = "";
      this.frmData.desc_ar = "";
      this.frmData.desc_en = "";
      this.frmData.code = "";
      this.frmData.from_price = "";
      this.frmData.to_price = "";
      this.frmData.ordering = "";
      this.initSelectDefaults();

      const query = { ...this.$route.query, page: 1 };
      QUERY_KEYS.forEach((key) => delete query[key]);
      this.$router.push({ query }).catch(() => {});
    },
  },
};
</script>

<style lang="scss">
.products-filter {
  --pf-radius: 14px;
  --pf-radius-sm: 10px;
  --pf-border: #e4e9ef;
  --pf-bg: #f5f7fa;
  --pf-card: #ffffff;
  --pf-text: #1a2332;
  --pf-muted: #6b7a8d;
  --pf-shadow: 0 4px 20px rgba(26, 35, 50, 0.06);
  --pf-control-h: 44px;
  --pf-gap: 16px;
  --pf-section-gap: 20px;

  padding: 0 16px;
  margin-bottom: 16px;

  .filter-card {
    background: var(--pf-card);
    border: 1px solid var(--pf-border);
    border-radius: var(--pf-radius);
    box-shadow: var(--pf-shadow);
    overflow: visible;

    &__head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: var(--pf-gap);
      padding: 16px 20px;
      border-bottom: 1px solid var(--pf-border);
      background: var(--pf-bg);
      border-radius: var(--pf-radius) var(--pf-radius) 0 0;
    }

    &__title {
      display: inline-flex;
      align-items: center;
      gap: 10px;
      font-size: 15px;
      font-weight: 700;
      color: var(--pf-text);
      line-height: 1.3;
    }

    &__title-icon {
      flex-shrink: 0;
      color: var(--mainColor-2);
    }

    &__badge {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 24px;
      height: 24px;
      padding: 0 8px;
      border-radius: 12px;
      background: var(--mainColor-2);
      color: #fff;
      font-size: 11px;
      font-weight: 700;
      margin-inline-start: 2px;
    }

    &__actions {
      display: flex;
      align-items: center;
      gap: 6px;
      flex-shrink: 0;
    }
  }

  .filter-link-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    min-height: 36px;
    padding: 8px 14px;
    border: 1px solid transparent;
    border-radius: var(--pf-radius-sm);
    background: transparent;
    color: var(--mainColor-2);
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s, border-color 0.2s;

    &:hover:not(:disabled) {
      background: rgba(39, 60, 152, 0.08);
      border-color: rgba(39, 60, 152, 0.15);
    }

    &:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }

    &--reset {
      color: #c62828;

      &:hover:not(:disabled) {
        background: #ffebee;
        border-color: #ffcdd2;
      }
    }
  }

  .filter-body {
    padding: 20px;
    overflow: visible;
  }

  .filter-section {
    position: relative;
    z-index: 0;
    padding-bottom: var(--pf-section-gap);
    margin-bottom: var(--pf-section-gap);
    border-bottom: 1px dashed var(--pf-border);

    &:has(.multiselect--active),
    &:has(.vue-treeselect--open) {
      z-index: 20;
    }

    &--last {
      padding-bottom: 0;
      margin-bottom: 0;
      border-bottom: none;
    }

    &__grid {
      display: grid;
      grid-template-columns: repeat(4, minmax(0, 1fr));
      gap: var(--pf-gap);
      overflow: visible;

      &--primary {
        grid-template-columns: 2fr 1fr;
      }

      &--compact {
        grid-template-columns: repeat(3, minmax(0, 1fr));
      }

      &--sub {
        margin-top: var(--pf-gap);
      }

      @media (max-width: 1200px) {
        grid-template-columns: repeat(3, minmax(0, 1fr));

        &--primary {
          grid-template-columns: 1.5fr 1fr;
        }
      }

      @media (max-width: 768px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));

        &--primary,
        &--compact {
          grid-template-columns: 1fr;
        }
      }

      @media (max-width: 520px) {
        grid-template-columns: 1fr;
      }
    }
  }

  .filter-control {
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
    position: relative;
    z-index: 1;

    &--wide {
      grid-column: span 1;
    }

    &--select {
      &:has(.multiselect--active),
      &:has(.vue-treeselect--open),
      &:focus-within {
        z-index: 100;
      }
    }

    &__label {
      display: block;
      margin: 0;
      padding: 0 2px;
      font-size: 12px;
      font-weight: 700;
      line-height: 1.3;
      color: var(--pf-muted);
      letter-spacing: 0.02em;

      &--category {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        color: var(--mainColor-2);
      }
    }

    &__label-icon {
      flex-shrink: 0;
      color: var(--mainColor-2);
      opacity: 0.9;
    }

    &__input {
      min-height: var(--pf-control-h);
      padding: 0 12px;
      border: 1px solid var(--pf-border);
      border-radius: var(--pf-radius-sm);
      background: var(--pf-card);
      transition: border-color 0.2s, box-shadow 0.2s;

      &:focus-within {
        border-color: var(--mainColor-2);
        box-shadow: 0 0 0 3px rgba(39, 60, 152, 0.1);
      }

      .v-text-field {
        margin: 0;
        padding: 0;
      }

      .v-input__control {
        min-height: calc(var(--pf-control-h) - 2px) !important;
      }

      .v-input__slot {
        min-height: calc(var(--pf-control-h) - 2px) !important;
        margin-bottom: 0 !important;
        padding: 0 !important;
        border: none !important;
        box-shadow: none !important;
        background: transparent !important;
      }

      fieldset {
        border: none !important;
      }

      .v-text-field__details {
        display: none;
      }

      .multiselect {
        min-height: calc(var(--pf-control-h) - 2px);
        margin: 0;

        .multiselect__select {
          height: calc(var(--pf-control-h) - 2px);
          width: 36px;
        }

        .multiselect__tags {
          min-height: calc(var(--pf-control-h) - 2px);
          padding: 8px 36px 0 0;
          border: none;
          background: transparent;
        }

        .multiselect__single,
        .multiselect__placeholder,
        .multiselect__input {
          margin-bottom: 0;
          padding: 0;
          font-size: 14px;
          line-height: 1.4;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .multiselect__content-wrapper {
          z-index: 120;
          margin-top: 6px;
          border: 1px solid var(--pf-border);
          border-radius: var(--pf-radius-sm);
          box-shadow: 0 10px 28px rgba(26, 35, 50, 0.14);
        }

        &.multiselect--active {
          z-index: 120;
        }

        .multiselect__option {
          min-height: 40px;
          padding: 10px 14px;
          font-size: 14px;
          line-height: 1.45;
          white-space: normal;
          word-break: break-word;

          &:first-child {
            font-weight: 600;
            color: var(--mainColor-2);
          }
        }
      }

      .vue-treeselect {
        margin: 0;

        .vue-treeselect__control {
          min-height: calc(var(--pf-control-h) - 2px);
          height: calc(var(--pf-control-h) - 2px);
          border: none !important;
          background: transparent !important;
          box-shadow: none !important;
          padding: 0;
        }

        &:hover .vue-treeselect__control,
        &.vue-treeselect--focused .vue-treeselect__control,
        &.vue-treeselect--open .vue-treeselect__control,
        &.vue-treeselect--open.vue-treeselect--open-below .vue-treeselect__control,
        &.vue-treeselect--open.vue-treeselect--open-above .vue-treeselect__control {
          border: none !important;
          box-shadow: none !important;
          background: transparent !important;
        }

        .vue-treeselect__placeholder,
        .vue-treeselect__single-value {
          font-size: 14px;
          line-height: calc(var(--pf-control-h) - 2px);
          padding: 0;
        }

        .vue-treeselect__menu-container {
          z-index: 120;
        }

        &.vue-treeselect--open {
          z-index: 120;
        }
      }

      &--treeselect {
        position: relative;
        padding: 0 12px;
        overflow: visible;
        isolation: isolate;

        &:focus-within {
          border-color: var(--pf-border);
          box-shadow: none;
        }

        &:has(.vue-treeselect--open) {
          border-color: var(--mainColor-2);
          box-shadow: 0 0 0 3px rgba(39, 60, 152, 0.1);
        }

        &:has(.filter-category-treeselect--filtered):not(:has(.vue-treeselect--open)) {
          border-color: rgba(39, 60, 152, 0.35);
          background: rgba(39, 60, 152, 0.04);
        }
      }
    }

    &--category {
      z-index: 1;

      .filter-control__label {
        position: relative;
        z-index: 2;
      }

      .filter-category-treeselect.vue-treeselect {
        position: relative;
        display: block;
        width: 100%;
        line-height: 1;

        .vue-treeselect__control {
          display: table !important;
          table-layout: fixed;
          width: 100%;
          height: calc(var(--pf-control-h) - 2px);
          min-height: calc(var(--pf-control-h) - 2px);
          padding: 0;
          cursor: pointer;
          border: none !important;
          background: transparent !important;
          box-shadow: none !important;
        }

        .vue-treeselect__value-container {
          display: table-cell !important;
          position: relative;
          width: 100%;
          height: calc(var(--pf-control-h) - 2px);
          vertical-align: middle;
          overflow: hidden;
        }

        .vue-treeselect__single-value,
        .vue-treeselect__placeholder {
          position: absolute;
          top: 0;
          right: 0;
          bottom: 0;
          left: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          padding: 0;
          margin: 0;
          line-height: calc(var(--pf-control-h) - 2px);
          color: var(--pf-text);
          font-size: 14px;
          font-weight: 500;
          pointer-events: none;
        }

        .vue-treeselect__placeholder {
          color: var(--pf-muted);
          font-weight: 400;
        }

        &.filter-category-treeselect--filtered:not(.vue-treeselect--open) .vue-treeselect__single-value {
          color: var(--mainColor-2);
          font-weight: 600;
        }

        .vue-treeselect__control-arrow-container {
          display: table-cell !important;
          width: 32px;
          min-width: 32px;
          height: calc(var(--pf-control-h) - 2px);
          vertical-align: middle;
          text-align: center;
          line-height: 0;
          padding: 0;
          background: transparent;
          border: none;

          .vue-treeselect__control-arrow {
            display: none;
          }

          &::before {
            content: "\f078";
            font-family: "Font Awesome 5 Pro", "Font Awesome 6 Pro", sans-serif;
            font-weight: 400;
            font-size: 12px;
            color: var(--pf-muted);
            line-height: 1;
          }
        }

        &.vue-treeselect--open .vue-treeselect__control-arrow-container {
          transform: rotate(180deg);

          &::before {
            color: var(--mainColor-2);
          }
        }

        .vue-treeselect__menu-container {
          position: absolute;
          top: 100%;
          left: 0;
          width: 100%;
          min-width: 280px;
          z-index: 120;
          margin-top: 4px;
        }

        &.vue-treeselect--open-above .vue-treeselect__menu-container {
          top: auto;
          bottom: 100%;
          margin-top: 0;
          margin-bottom: 4px;
        }

        .vue-treeselect__menu {
          max-height: 320px;
          padding: 6px;
          background: var(--pf-card);
          border: 1px solid var(--pf-border);
          border-radius: var(--pf-radius-sm);
          box-shadow: 0 10px 28px rgba(26, 35, 50, 0.14);
        }

        .vue-treeselect__list {
          max-height: 300px;
          overflow-y: auto;
          padding: 0;
        }

        .vue-treeselect__option {
          border-radius: 8px;
          margin-bottom: 2px;

          &--highlight {
            background: rgba(39, 60, 152, 0.08);
          }

          &--selected {
            background: rgba(39, 60, 152, 0.12);

            .vue-treeselect__label {
              color: var(--mainColor-2);
              font-weight: 600;
            }
          }
        }

        .vue-treeselect__label-container {
          padding: 10px 12px;
        }

        .vue-treeselect__label {
          font-size: 14px;
          line-height: 1.45;
          color: var(--pf-text);
        }

        .vue-treeselect__count {
          display: inline-flex;
          align-items: center;
          min-width: 22px;
          height: 20px;
          padding: 0 7px;
          margin-inline-start: 8px;
          border-radius: 10px;
          background: var(--pf-bg);
          color: var(--pf-muted);
          font-size: 11px;
          font-weight: 600;
        }

        .vue-treeselect__option:first-child .vue-treeselect__label {
          font-weight: 700;
          color: var(--mainColor-2);
        }

        @for $level from 1 through 5 {
          .vue-treeselect__indent-level-#{$level} .vue-treeselect__label-container {
            padding-inline-start: #{12 + ($level * 14)}px;
          }
        }
      }
    }
  }

  .filter-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 24px;
    padding-top: 18px;
    border-top: 1px solid var(--pf-border);
  }

  .filter-apply-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-width: 168px;
    height: 44px;
    padding: 0 22px;
    border: none;
    border-radius: var(--pf-radius-sm);
    background: var(--mainColor-2);
    color: #fff;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    box-shadow: 0 4px 14px rgba(39, 60, 152, 0.28);
    transition: filter 0.2s, transform 0.15s;

    &:hover {
      filter: brightness(1.05);
    }

    &:active {
      transform: scale(0.98);
    }
  }

  &--rtl,
  &[dir="rtl"] {
    .filter-card__title,
    .filter-link-btn,
    .filter-apply-btn,
    .filter-control__label {
      direction: rtl;
    }

    .filter-footer {
      justify-content: flex-start;
    }

    .filter-control__input {
      .multiselect {
        direction: rtl;
        text-align: right;

        .multiselect__select {
          left: 1px;
          right: auto;
        }

        .multiselect__tags {
          padding: 8px 0 0 36px;
          text-align: right;
        }

        .multiselect__single,
        .multiselect__placeholder,
        .multiselect__input,
        .multiselect__option {
          text-align: right;
          direction: rtl;
        }

        .multiselect__content-wrapper {
          direction: rtl;
          text-align: right;
        }
      }

      .vue-treeselect {
        text-align: right;

        .vue-treeselect__control {
          direction: rtl;
        }

        .vue-treeselect__placeholder,
        .vue-treeselect__single-value,
        .vue-treeselect__label {
          text-align: right;
        }

        .vue-treeselect__menu {
          direction: rtl;
          text-align: right;
        }

        .vue-treeselect__input {
          direction: rtl;
          text-align: right;
        }

        .vue-treeselect__option-arrow-container {
          margin-left: 0;
          margin-right: auto;
        }
      }

      .filter-control--category .filter-category-treeselect.vue-treeselect {
        .vue-treeselect__control {
          direction: rtl;
        }
      }
    }
  }

  &[dir="ltr"] {
    .filter-footer {
      justify-content: flex-end;
    }

    .filter-control__input {
      .multiselect {
        direction: ltr;
        text-align: left;

        .multiselect__tags {
          padding: 8px 36px 0 0;
          text-align: left;
        }

        .multiselect__single,
        .multiselect__placeholder,
        .multiselect__input,
        .multiselect__option {
          text-align: left;
          direction: ltr;
        }
      }

      .vue-treeselect__placeholder,
      .vue-treeselect__single-value,
      .vue-treeselect__label {
        text-align: left;
      }
    }
  }
}

body.darkTheme .products-filter {
  --pf-border: #3a3f4b;
  --pf-bg: #1e2128;
  --pf-card: #262a33;
  --pf-text: #e8eaed;
  --pf-muted: #9aa0a6;
  --pf-shadow: 0 4px 20px rgba(0, 0, 0, 0.35);

  .filter-card {
    background: var(--pf-card);

    &__head {
      background: var(--pf-bg);
    }
  }

  .filter-control__input {
    background: #2a2f38;

    .multiselect__content-wrapper,
    .vue-treeselect__menu {
      background: var(--pf-card);
      border-color: var(--pf-border);
    }
  }

  .filter-control--category .filter-category-treeselect.vue-treeselect {
    .vue-treeselect__menu {
      background: var(--pf-card);
    }

    .vue-treeselect__option--highlight {
      background: rgba(255, 255, 255, 0.06);
    }

    .vue-treeselect__option--selected {
      background: rgba(39, 60, 152, 0.25);
    }

    .vue-treeselect__count {
      background: #1e2128;
    }
  }

  .filter-control__input--treeselect {
    &:has(.filter-category-treeselect--filtered):not(:has(.vue-treeselect--open)) {
      background: rgba(39, 60, 152, 0.12);
    }
  }
}
</style>
